import React, { useState, useEffect, useRef } from 'react';
import {
  Upload, CheckCircle, AlertTriangle, ChevronDown, ChevronUp,
  Eye, Trash2, RefreshCw, Package, FileSpreadsheet, Check
} from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import { vendorImportApi } from '../../services/api';
import api from '../../services/api';
import Header from '../shared/Header';

const STATUS_BADGE = {
  pending: 'bg-yellow-100 text-yellow-800',
  duplicate: 'bg-orange-100 text-orange-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const BATCH_STATUS_BADGE = {
  pending: 'bg-yellow-100 text-yellow-800',
  partial: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
};

// ── Helper ────────────────────────────────────────────────────────────────────
function tryParse(str, fallback) {
  try { return JSON.parse(str); } catch { return fallback; }
}

// ── Upload Panel ──────────────────────────────────────────────────────────────
// Returns 'sheaura' | 'baanganga' | 'modi' | 'gururaj' | null based on vendor name
function resolveParser(vendorName) {
  const n = (vendorName || '').toLowerCase();
  if (n.includes('sheaura')) return 'sheaura';
  if (n.includes('baanganga') || n.includes('baanga') || n.includes('aakshvi')) return 'baanganga';
  if (n.includes('modi')) return 'modi';
  if (n.includes('raj diamond')) return 'gururaj'; // matches both "Raj Diamonds" and "Guru Raj Diamonds"
  return null;
}

const PARSER_BADGE = {
  sheaura: 'Sheaura format',
  baanganga: 'Baanganga format',
  modi: 'Modi format',
  gururaj: 'Raj Diamonds PDF format',
};

function UploadPanel({ vendors }) {
  const [file, setFile] = useState(null);
  const [vendorId, setVendorId] = useState('');
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef();

  const selectedVendor = vendors.find(v => String(v.id) === String(vendorId)) || null;
  const parser = selectedVendor ? resolveParser(selectedVendor.name) : null;
  const canUpload = !!vendorId && !!parser && !!file && !uploading;

  const handleFile = (f) => {
    if (!f) return;
    const expectsPdf = parser === 'gururaj';
    const validExt = expectsPdf ? /\.pdf$/i.test(f.name) : /\.(xlsx|xls)$/i.test(f.name);
    if (!validExt) {
      setError(expectsPdf ? 'Only .pdf files supported for this vendor' : 'Only .xlsx / .xls files supported');
      return;
    }
    setFile(f); setError(''); setResult(null);
  };

  const handleUpload = async () => {
    if (!vendorId) { setError('Please select a vendor first.'); return; }
    if (!parser) { setError('No import parser available for the selected vendor.'); return; }
    if (!file) return;
    setUploading(true); setError(''); setResult(null);
    try {
      const res = await vendorImportApi.upload(file, vendorId);
      if (res && res.batch_id != null) { setResult(res); }
      else setError('Upload failed — unexpected response');
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Vendor <span className="text-red-500">*</span>
        </label>
        <select
          value={vendorId}
          onChange={e => { setVendorId(e.target.value); setError(''); setResult(null); }}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="">— Select vendor —</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>

        {/* Parser status */}
        {vendorId && parser && (
          <p className="mt-1.5 text-xs text-green-600 font-medium">
            ✓ {PARSER_BADGE[parser]} will be used
          </p>
        )}
        {vendorId && !parser && (
          <p className="mt-1.5 text-xs text-red-500 font-medium">
            ✗ No import parser available for this vendor — only Sheaura, Baanganga, Aakshvi and Raj Diamonds are supported
          </p>
        )}
      </div>

      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
        onClick={() => inputRef.current.click()}
        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${dragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
          }`}
      >
        <input ref={inputRef} type="file" accept={parser === 'gururaj' ? '.pdf' : '.xlsx,.xls'} className="hidden"
          onChange={e => handleFile(e.target.files[0])} />
        <FileSpreadsheet className="mx-auto mb-3 text-gray-400" size={40} />
        {file ? (
          <p className="text-sm font-medium text-blue-700">{file.name}</p>
        ) : (
          <>
            <p className="text-sm font-medium text-gray-700">Drop packing-list {parser === 'gururaj' ? 'PDF' : 'Excel'} here</p>
            <p className="text-xs text-gray-400 mt-1">or click to browse — {parser === 'gururaj' ? '.pdf' : '.xlsx / .xls'}</p>
          </>
        )}
      </div>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      <button
        onClick={handleUpload}
        disabled={!canUpload}
        className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {uploading ? <><RefreshCw size={16} className="animate-spin" /> Parsing…</> : <><Upload size={16} /> Upload & Parse</>}
      </button>

      {result && (
        <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 text-sm">
          <p className="font-semibold text-green-800 mb-2">
            <CheckCircle size={16} className="inline mr-1" /> Parsed successfully
          </p>
          <div className="grid grid-cols-2 gap-1 text-green-700">
            <span>ESTM No:</span><span className="font-medium">{result.estm_no || '—'}</span>
            <span>Date:</span><span className="font-medium">{result.import_date || '—'}</span>
            <span>Total items:</span><span className="font-medium">{result.total_items}</span>
            <span>Duplicates flagged:</span><span className="font-medium text-orange-600">{result.duplicates}</span>
          </div>
          <p className="mt-2 text-green-600 text-xs">Switch to the Review tab to assign categories and approve.</p>
        </div>
      )}
    </div>
  );
}

// ── Item Table Row ────────────────────────────────────────────────────────────
function ItemTableRow({ item, categories, onUpdate, onApprove, onDelete, approving }) {
  const [expanded, setExpanded] = useState(false);
  const [catId, setCatId] = useState(item.category_id || '');
  const [notes, setNotes] = useState(item.notes || '');
  const [netWt, setNetWt] = useState(item.net_weight ?? '');
  const [saving, setSaving] = useState(false);
  const [localStatus, setLocalStatus] = useState(item.import_status);

  const diamonds = Array.isArray(item.diamonds) ? item.diamonds : tryParse(item.diamonds, []);
  const stones = Array.isArray(item.stones) ? item.stones : tryParse(item.stones, []);
  const chain = item.chain_info
    ? (typeof item.chain_info === 'object' ? item.chain_info : tryParse(item.chain_info, null))
    : null;

  const totalDiaCt = diamonds.reduce((s, d) => s + (d.weight_ct || 0), 0)
    + (chain?.diamonds || []).reduce((s, d) => s + (d.weight_ct || 0), 0);
  const totalStnCt = stones.reduce((s, s2) => s + (s2.weight_ct || 0), 0)
    + (chain?.stones || []).reduce((s, s2) => s + (s2.weight_ct || 0), 0);

  // Net weight verification must convert each entry by its OWN unit —
  // gram-denominated stones (e.g. BB/Black Beads) are not carats and must
  // not be multiplied by 0.2, or their real weight gets under-deducted.
  const entryGrams = (e) => {
    const wt = e.weight_ct || 0;
    if (e.unit === 'gram') return wt;
    if (e.unit === 'number') return 0;
    return wt * 0.2; // carat (default — also applies to chain sub-items, which carry no unit)
  };
  const totalAllG = diamonds.reduce((s, d) => s + entryGrams(d), 0)
    + stones.reduce((s, s2) => s + entryGrams(s2), 0)
    + (chain?.diamonds || []).reduce((s, d) => s + (d.weight_ct || 0) * 0.2, 0)
    + (chain?.stones || []).reduce((s, s2) => s + (s2.weight_ct || 0) * 0.2, 0);

  // Modi's Kundan gold weight (chain.kun_wt) is part of the item's gross
  // weight too, same as stones — just gold instead of a stone — so it must
  // be deducted here as well, or every Modi item with Kundan gold falsely
  // flags a mismatch equal to exactly that weight.
  const kunWt = chain?.type === 'modi' ? (parseFloat(chain.kun_wt) || 0) : 0;

  const grossWt = parseFloat(item.gross_weight) || 0;
  const netWtExcel = parseFloat(netWt) || 0;
  const netWtMissing = netWtExcel <= 0;
  const netWtCalc = parseFloat((grossWt - totalAllG - kunWt).toFixed(3));
  const netWtDiff = parseFloat((netWtExcel - netWtCalc).toFixed(3));
  const netMismatch = !netWtMissing && Math.abs(netWtDiff) > 0.005;

  const certActual = parseFloat(item.certificate_charges) || 0;
  const certExpected = parseFloat(item.cert_expected) || 0;
  const certMismatch = item.cert_mismatch;

  const isDone = localStatus === 'approved' || localStatus === 'rejected';

  const handleSave = async () => {
    if (!catId) return;
    setSaving(true);
    await onUpdate(item.id, { category_id: parseInt(catId), notes, net_weight: netWtExcel > 0 ? netWtExcel : null });
    setSaving(false);
  };

  const handleApprove = async () => {
    if (!catId) { alert('Please select a category first'); return; }
    if (netWtMissing) { alert('Net weight is missing — enter it before approving'); return; }
    await onUpdate(item.id, { category_id: parseInt(catId), notes, net_weight: netWtExcel });
    const ok = await onApprove(item.id);
    if (ok) setLocalStatus('approved');
  };

  const rowBg =
    localStatus === 'approved' ? 'bg-green-50' :
      localStatus === 'duplicate' ? 'bg-orange-50' :
        localStatus === 'rejected' ? 'bg-red-50' : 'bg-white';

  return (
    <>
      <tr className={`border-b border-gray-100 text-sm hover:bg-opacity-80 ${rowBg}`}>
        {/* Expand + Sr# */}
        <td className="px-2 py-2 text-center whitespace-nowrap">
          <button onClick={() => setExpanded(!expanded)} className="text-gray-400 hover:text-gray-700 mr-1">
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
          <span className="text-xs text-gray-500">{item.sr_no}</span>
        </td>

        {/* Description */}
        <td className="px-3 py-2">
          <div className="font-medium text-gray-800 text-xs">{item.description}</div>
          {chain && <div className="text-xs text-blue-600 mt-0.5">+Chain {chain.net_weight_g}g</div>}
        </td>

        {/* Kt */}
        <td className="px-2 py-2 text-xs text-gray-600 whitespace-nowrap">{item.kt}</td>

        {/* Gr Wt */}
        <td className="px-2 py-2 text-xs text-right text-gray-600 whitespace-nowrap">
          {parseFloat(item.gross_weight).toFixed(3)}
        </td>

        {/* Net Wt (from Excel, or missing — needs manual entry) */}
        <td className={`px-2 py-2 text-xs text-right whitespace-nowrap ${netWtMissing ? 'text-red-600 font-semibold' : netMismatch ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
          {netWtMissing ? (
            <span className="flex items-center justify-end gap-1">
              <AlertTriangle size={11} className="text-red-500" /> missing
            </span>
          ) : netWtExcel.toFixed(3)}
        </td>

        {/* Net Wt calculated (Gross − all CT × 0.2) */}
        <td className={`px-2 py-2 text-xs text-right whitespace-nowrap ${netMismatch ? 'text-red-500' : 'text-gray-400'}`}>
          {netWtMissing ? (
            <span className="text-gray-300">—</span>
          ) : netMismatch ? (
            <span className="flex items-center justify-end gap-1">
              <AlertTriangle size={11} className="text-red-500" />
              {netWtCalc.toFixed(3)}
            </span>
          ) : (
            <span className="text-green-500">✓</span>
          )}
        </td>

        {/* Diamonds / Stones CT */}
        <td className="px-2 py-2 text-xs text-right whitespace-nowrap">
          {totalDiaCt > 0
            ? <span className="text-blue-700 font-medium">{totalDiaCt.toFixed(3)}</span>
            : <span className="text-gray-300">—</span>}
          {totalStnCt > 0 && (
            <div className="text-purple-600">{totalStnCt.toFixed(3)} st</div>
          )}
        </td>

        {/* Cert actual (from Excel) */}
        <td className={`px-2 py-2 text-xs text-right whitespace-nowrap ${certMismatch ? 'text-orange-600 font-semibold' : 'text-gray-600'}`}>
          {certActual > 0 ? `₹${certActual.toFixed(0)}` : <span className="text-gray-300">—</span>}
        </td>

        {/* Cert calculated (ct × 750) */}
        <td className={`px-2 py-2 text-xs text-right whitespace-nowrap ${certMismatch ? 'text-orange-500' : 'text-gray-400'}`}>
          {certExpected > 0 ? (
            <span className="flex items-center justify-end gap-1">
              {certMismatch && <AlertTriangle size={11} className="text-orange-500" />}
              ₹{certExpected.toFixed(0)}
            </span>
          ) : <span className="text-gray-300">—</span>}
        </td>

        {/* Total INR */}
        <td className="px-2 py-2 text-xs text-right font-semibold text-gray-800 whitespace-nowrap">
          ₹{parseInt(item.total_inr || 0).toLocaleString('en-IN')}
        </td>

        {/* Status */}
        <td className="px-2 py-2 whitespace-nowrap">
          <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[localStatus] || 'bg-gray-100 text-gray-600'}`}>
            {localStatus}
          </span>
          {localStatus === 'approved' && item.assigned_code && (
            <div className="text-xs text-green-700 font-medium mt-0.5">{item.assigned_code}</div>
          )}
          {localStatus === 'duplicate' && item.conflict_code && (
            <div className="text-xs text-orange-700 font-medium mt-0.5">{item.conflict_code}</div>
          )}
        </td>

        {/* Category */}
        <td className="px-2 py-2">
          {!isDone ? (
            <select
              value={catId}
              onChange={e => setCatId(e.target.value)}
              className="w-28 border border-gray-300 rounded px-1.5 py-1 text-xs focus:ring-2 focus:ring-blue-500"
            >
              <option value="">— Select —</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
              ))}
            </select>
          ) : (
            <span className="text-xs text-gray-500">{item.category_name || '—'}</span>
          )}
        </td>

        {/* Actions */}
        <td className="px-2 py-2 whitespace-nowrap">
          {!isDone && (
            <div className="flex gap-1">
              <button
                onClick={handleApprove}
                disabled={approving || !catId || netWtMissing}
                title="Approve"
                className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-40 flex items-center gap-0.5"
              >
                <Check size={11} /> OK
              </button>
              <button
                onClick={() => onDelete(item.id)}
                title="Remove"
                className="px-1.5 py-1 text-xs text-red-500 hover:bg-red-50 rounded"
              >
                <Trash2 size={12} />
              </button>
            </div>
          )}
        </td>
      </tr>

      {/* Expanded detail row */}
      {expanded && (
        <tr className={`border-b border-gray-200 ${rowBg}`}>
          <td colSpan={13} className="px-6 py-4 bg-gray-50">
            <div className="space-y-4">

              {/* Duplicate conflict */}
              {localStatus === 'duplicate' && item.conflict_code && (
                <div className="bg-orange-50 border border-orange-200 rounded p-2 text-xs text-orange-700 flex items-start gap-2">
                  <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
                  <span>Matches an existing item already in inventory: <strong>{item.conflict_code}</strong></span>
                </div>
              )}

              {/* Diamonds table */}
              {diamonds.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Diamonds</p>
                  <table className="text-xs border-collapse">
                    <thead>
                      <tr className="text-gray-400 border-b border-gray-200">
                        <th className="pr-6 pb-1 text-left font-medium">Code</th>
                        <th className="pr-6 pb-1 text-right font-medium">Pcs</th>
                        <th className="pr-6 pb-1 text-right font-medium">Wt CT</th>
                        <th className="pr-6 pb-1 text-right font-medium">Rate ₹</th>
                        <th className="pr-6 pb-1 text-right font-medium">Amount ₹</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diamonds.map((d, i) => (
                        <tr key={i} className="text-gray-700">
                          <td className="pr-6 py-0.5 font-medium text-blue-700">{d.code}</td>
                          <td className="pr-6 py-0.5 text-right">{d.pcs}</td>
                          <td className="pr-6 py-0.5 text-right">{d.weight_ct}</td>
                          <td className="pr-6 py-0.5 text-right">{(d.rate || 0).toLocaleString('en-IN')}</td>
                          <td className="pr-6 py-0.5 text-right">{(d.amount || 0).toLocaleString('en-IN')}</td>
                        </tr>
                      ))}
                      <tr className="font-semibold text-gray-700 border-t border-gray-200 mt-1">
                        <td className="pr-6 pt-1">Total</td>
                        <td className="pr-6 pt-1 text-right">{diamonds.reduce((s, d) => s + d.pcs, 0)}</td>
                        <td className="pr-6 pt-1 text-right text-blue-700">{totalDiaCt.toFixed(3)}</td>
                        <td></td>
                        <td className="pr-6 pt-1 text-right">{diamonds.reduce((s, d) => s + (d.amount || 0), 0).toLocaleString('en-IN')}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Stones table */}
              {stones.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Stones</p>
                  <table className="text-xs border-collapse">
                    <thead>
                      <tr className="text-gray-400 border-b border-gray-200">
                        <th className="pr-6 pb-1 text-left font-medium">Code</th>
                        <th className="pr-6 pb-1 text-right font-medium">Pcs</th>
                        <th className="pr-6 pb-1 text-right font-medium">Wt CT</th>
                        <th className="pr-6 pb-1 text-right font-medium">Rate ₹</th>
                        <th className="pr-6 pb-1 text-right font-medium">Amount ₹</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stones.map((s, i) => (
                        <tr key={i} className="text-gray-700">
                          <td className="pr-6 py-0.5 font-medium text-purple-700">{s.code}</td>
                          <td className="pr-6 py-0.5 text-right">{s.pcs}</td>
                          <td className="pr-6 py-0.5 text-right">{s.weight_ct}</td>
                          <td className="pr-6 py-0.5 text-right">{(s.rate || 0).toLocaleString('en-IN')}</td>
                          <td className="pr-6 py-0.5 text-right">{(s.amount || 0).toLocaleString('en-IN')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Chain details */}
              {chain && (
                <div className="bg-blue-50 rounded p-2 text-xs text-blue-800">
                  <span className="font-semibold">Chain:</span> {chain.item_no} ({chain.kt}) · Net {chain.net_weight_g}g · Gold Rate ₹{chain.gold_rate}
                  {(chain.diamonds || []).length > 0 && (
                    <span className="ml-2">| Diamonds: {chain.diamonds.map(d => `${d.code} ${d.weight_ct}ct`).join(', ')}</span>
                  )}
                  {(chain.stones || []).length > 0 && (
                    <span className="ml-2">| Stones: {chain.stones.map(s => `${s.code} ${s.weight_ct}ct`).join(', ')}</span>
                  )}
                </div>
              )}

              {/* Net weight missing — no purity-matching column found in the sheet; needs manual entry */}
              {netWtMissing && !isDone && (
                <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700 flex items-start gap-2">
                  <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
                  <div className="flex items-center gap-2">
                    <span>Net weight missing (no matching purity column for {item.kt} in the sheet) — enter it manually:</span>
                    <input
                      type="number"
                      step="0.001"
                      value={netWt}
                      onChange={e => setNetWt(e.target.value)}
                      placeholder="grams"
                      className="w-24 border border-red-300 rounded px-2 py-1 text-xs"
                    />
                  </div>
                </div>
              )}

              {/* Net weight verification */}
              {netMismatch && (
                <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700 flex items-start gap-2">
                  <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
                  <span>
                    Net weight mismatch — Excel: <strong>{netWtExcel.toFixed(3)} g</strong> · Calculated (Gross {grossWt.toFixed(3)} − stones/diamonds {totalAllG.toFixed(3)} g{kunWt > 0 ? ` − Kundan ${kunWt.toFixed(3)} g` : ''}): <strong>{netWtCalc.toFixed(3)} g</strong> · Diff: <strong>{netWtDiff > 0 ? '+' : ''}{netWtDiff.toFixed(3)} g</strong>
                  </span>
                </div>
              )}

              {/* Cert verification */}
              {certMismatch && (
                <div className="bg-orange-50 border border-orange-200 rounded p-2 text-xs text-orange-700 flex items-start gap-2">
                  <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
                  <span>
                    Certificate mismatch — Excel: <strong>₹{certActual}</strong> · Calculated ({totalDiaCt.toFixed(3)} ct × ₹750): <strong>₹{certExpected}</strong>
                  </span>
                </div>
              )}

              {/* Gold & Labour summary */}
              <div className="flex flex-wrap gap-6 text-xs text-gray-600">
                <div><span className="text-gray-400">Gold Rate:</span> ₹{parseInt(item.gold_rate || 0).toLocaleString('en-IN')}/g</div>
                <div><span className="text-gray-400">Gold Amt:</span> ₹{parseInt(item.gold_amount || 0).toLocaleString('en-IN')}</div>
                <div><span className="text-gray-400">Labour Rate:</span> ₹{parseInt(item.labour_rate || 0).toLocaleString('en-IN')}/g</div>
                <div><span className="text-gray-400">Labour Amt:</span> ₹{parseInt(item.labour_amount || 0).toLocaleString('en-IN')}</div>
                {certActual > 0 && <div><span className="text-gray-400">Cert:</span> ₹{certActual}</div>}
              </div>

              {/* Notes */}
              {!isDone && (
                <div className="flex items-center gap-2 pt-1">
                  <label className="text-xs text-gray-500 flex-shrink-0">Notes:</label>
                  <input
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Optional notes…"
                    className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs"
                  />
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-40"
                  >
                    {saving ? 'Saving…' : 'Save Notes'}
                  </button>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Batch Review Panel ────────────────────────────────────────────────────────
function BatchReviewPanel({ categories }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBatch, setActiveBatch] = useState(null);
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [approvingId, setApprovingId] = useState(null);

  const loadBatches = async () => {
    setLoading(true);
    try {
      const res = await vendorImportApi.getBatches();
      setBatches(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error('loadBatches error:', e);
    }
    setLoading(false);
  };

  const loadItems = async (batchId) => {
    setLoadingItems(true);
    try {
      const res = await vendorImportApi.getBatchItems(batchId);
      setItems(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error('loadItems error:', e);
    }
    setLoadingItems(false);
  };

  useEffect(() => { loadBatches(); }, []);

  const handleSelectBatch = (batch) => {
    setActiveBatch(batch);
    loadItems(batch.id);
    setFilterStatus('all');
  };

  const handleUpdate = async (itemId, updates) => {
    await vendorImportApi.updateItem(itemId, updates);
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, ...updates } : i));
  };

  const handleApprove = async (itemId) => {
    setApprovingId(itemId);
    try {
      const res = await vendorImportApi.approveItem(itemId);
      setItems(prev => prev.map(i => i.id === itemId
        ? { ...i, import_status: 'approved', assigned_code: res.assigned_code } : i));
      loadBatches();
      setApprovingId(null);
      return true;
    } catch (e) {
      alert(e.message || 'Approve failed');
      setApprovingId(null);
      return false;
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Remove this item from the staging list?')) return;
    await vendorImportApi.deleteItem(itemId);
    setItems(prev => prev.filter(i => i.id !== itemId));
    loadBatches();
  };

  const handleDeleteBatch = async (e, batchId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this entire import batch and all its items?')) return;
    try {
      await vendorImportApi.deleteBatch(batchId);
      if (activeBatch?.id === batchId) { setActiveBatch(null); setItems([]); }
      loadBatches();
    } catch (err) {
      alert(err.message || 'Delete failed');
    }
  };

  const handleBulkApprove = async () => {
    const eligible = items.filter(i => i.import_status === 'pending' && i.category_id && parseFloat(i.net_weight) > 0);
    if (!eligible.length) { alert('No pending items with a category assigned and a verified net weight.'); return; }
    if (!window.confirm(`Approve ${eligible.length} item(s)?`)) return;
    setBulkApproving(true);
    await vendorImportApi.approveSelected(activeBatch.id, eligible.map(i => i.id));
    await loadItems(activeBatch.id);
    loadBatches();
    setBulkApproving(false);
  };

  const filteredItems = items.filter(i => filterStatus === 'all' || i.import_status === filterStatus);

  // Count by status for filter buttons
  const counts = items.reduce((acc, i) => {
    acc[i.import_status] = (acc[i.import_status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="flex gap-4">
      {/* Batch list sidebar */}
      <div className="w-64 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700">Import Batches</h3>
          <button onClick={loadBatches} className="text-gray-400 hover:text-gray-700"><RefreshCw size={14} /></button>
        </div>
        {loading && <p className="text-xs text-gray-400 py-4 text-center">Loading…</p>}
        {!loading && !batches.length && <p className="text-xs text-gray-400 py-4 text-center">No imports yet</p>}
        <div className="space-y-2">
          {batches.map(b => (
            <button
              key={b.id}
              onClick={() => handleSelectBatch(b)}
              className={`w-full text-left px-3 py-3 rounded-lg border text-sm transition-colors ${activeBatch?.id === b.id ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
            >
              <div className="flex items-center justify-between mb-0.5">
                <span className="font-medium text-gray-800 truncate text-xs">{b.estm_no || `Batch #${b.id}`}</span>
                <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${BATCH_STATUS_BADGE[b.status] || 'bg-gray-100 text-gray-600'}`}>
                    {b.status}
                  </span>
                  <button
                    onClick={(e) => handleDeleteBatch(e, b.id)}
                    className="text-gray-300 hover:text-red-500 transition-colors p-0.5"
                    title="Delete batch"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500 truncate">{b.file_name}</p>
              <p className="text-xs text-gray-400">{b.import_date} · {b.vendor_name || 'No vendor'}</p>
              <div className="flex gap-2 mt-1 text-xs">
                <span className="text-green-600">{b.approved_count} approved</span>
                {b.duplicate_count > 0 && <span className="text-orange-500">{b.duplicate_count} dup</span>}
                {b.pending_count > 0 && <span className="text-yellow-600">{b.pending_count} pending</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Item table */}
      <div className="flex-1 min-w-0">
        {!activeBatch && (
          <div className="flex flex-col items-center justify-center h-48 text-gray-400">
            <Package size={32} className="mb-2" />
            <p className="text-sm">Select a batch to review items</p>
          </div>
        )}

        {activeBatch && (
          <>
            {/* Toolbar */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <h3 className="text-sm font-semibold text-gray-800">{activeBatch.estm_no || `Batch #${activeBatch.id}`}</h3>
              <span className="text-xs text-gray-400">{activeBatch.import_date}</span>

              {/* Status filter pills */}
              <div className="flex gap-1 ml-auto flex-wrap">
                {[
                  { key: 'all', label: `All (${items.length})` },
                  { key: 'pending', label: `Pending (${counts.pending || 0})` },
                  { key: 'duplicate', label: `Dup (${counts.duplicate || 0})` },
                  { key: 'approved', label: `Done (${counts.approved || 0})` },
                ].map(({ key, label }) => (
                  <button key={key}
                    onClick={() => setFilterStatus(key)}
                    className={`px-2 py-0.5 text-xs rounded-full ${filterStatus === key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <button
                onClick={handleBulkApprove}
                disabled={bulkApproving}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40"
              >
                {bulkApproving ? <RefreshCw size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                Approve All Pending
              </button>
            </div>

            {loadingItems && <p className="text-sm text-gray-400 py-8 text-center">Loading items…</p>}

            {!loadingItems && (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full border-collapse text-sm">
                  <thead className="bg-gray-50">
                    <tr className="border-b-2 border-gray-200">
                      <th className="px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide"># Sr</th>
                      <th className="px-3 py-2 text-left   text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</th>
                      <th className="px-2 py-2 text-left   text-xs font-semibold text-gray-500 uppercase tracking-wide">Kt</th>
                      <th className="px-2 py-2 text-right  text-xs font-semibold text-gray-500 uppercase tracking-wide">Gr Wt g</th>
                      <th className="px-2 py-2 text-right  text-xs font-semibold text-gray-500 uppercase tracking-wide" title="Net weight from Excel">Net Wt g</th>
                      <th className="px-2 py-2 text-right  text-xs font-semibold text-gray-500 uppercase tracking-wide" title="Calculated: Gross − (all stone CT × 0.2)">Calc Net</th>
                      <th className="px-2 py-2 text-right  text-xs font-semibold text-gray-500 uppercase tracking-wide">Dia CT</th>
                      <th className="px-2 py-2 text-right  text-xs font-semibold text-gray-500 uppercase tracking-wide" title="Certificate charges from Excel">Cert ₹</th>
                      <th className="px-2 py-2 text-right  text-xs font-semibold text-gray-500 uppercase tracking-wide" title="Calculated: Diamond CT × ₹700">Calc ₹</th>
                      <th className="px-2 py-2 text-right  text-xs font-semibold text-gray-500 uppercase tracking-wide">Total ₹</th>
                      <th className="px-2 py-2 text-left   text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                      <th className="px-2 py-2 text-left   text-xs font-semibold text-gray-500 uppercase tracking-wide">Category</th>
                      <th className="px-2 py-2 text-left   text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.length === 0 && (
                      <tr>
                        <td colSpan={13} className="text-center text-sm text-gray-400 py-8">
                          No items match this filter
                        </td>
                      </tr>
                    )}
                    {filteredItems.map(item => (
                      <ItemTableRow
                        key={item.id}
                        item={item}
                        categories={categories}
                        onUpdate={handleUpdate}
                        onApprove={handleApprove}
                        onDelete={handleDelete}
                        approving={approvingId === item.id}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function VendorImport() {
  const [tab, setTab] = useState('upload');
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.getVendors().then(res => setVendors(Array.isArray(res) ? res : (res?.data || [])));
    api.getCategories().then(res => {
      const cats = Array.isArray(res) ? res : (res?.data || []);
      setCategories(cats.filter(c => c.parent_id && c.code));
    });
  }, []);

  return (
    <div>
      <PageIdentifier pageId={SCREEN_IDS.ADMINISTRATION.VENDOR_IMPORT} pageName="Vendor Import" />
      <Header currentModule="vendor-import" />
      <div className="p-4">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-gray-200">
          {[
            { id: 'upload', label: 'Upload', icon: Upload },
            { id: 'review', label: 'Review & Approve', icon: Eye },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === id
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
            >
              <Icon size={16} />{label}
            </button>
          ))}
        </div>

        {tab === 'upload' && <UploadPanel vendors={vendors} />}
        {tab === 'review' && <BatchReviewPanel categories={categories} />}
      </div>
    </div>
  );
}
